/**
 * Seatbelt (macOS sandbox-exec)
 *
 * macOS built-in sandboxing using sandbox-exec with SBPL profiles.
 *
 * Important: Uses `-p` (inline profile) instead of `-f` (file) because
 * `-f` doesn't work reliably with path filters on modern macOS.
 *
 * Note on macOS sandbox limitations:
 * - `(allow file-read* (subpath ...))` only works WITH a preceding `(allow file-read*)`
 * - So for reads: allow all, then deny specific paths
 * - For writes: allow specific paths with subpath filters
 *
 * Based on the approach used by Claude Code's sandbox-runtime:
 * https://github.com/anthropic-experimental/sandbox-runtime
 */

import { realpathSync } from 'node:fs';

import type { NativeSandboxConfig } from './types';

/**
 * Mach services needed for basic operation
 */
const MACH_SERVICES = [
  'com.apple.distributed_notifications@Uv3',
  'com.apple.logd',
  'com.apple.system.logger',
  'com.apple.system.notification_center',
  'com.apple.system.opendirectoryd.libinfo',
  'com.apple.system.opendirectoryd.membership',
  'com.apple.bsd.dirhelper',
  'com.apple.securityd.xpc',
  'com.apple.SecurityServer',
  'com.apple.trustd.agent',
];

/**
 * SBPL comment that marks a profile as generated by Mastra.
 *
 * Written into every generated profile. A profile file that carries this marker is ours,
 * even when it sits at a path the user configured, so Mastra regenerates it instead of
 * treating it as user-authored SBPL.
 */
export const GENERATED_PROFILE_MARKER = '; mastra-generated-profile (regenerated on every start)';

/**
 * Check whether a profile file was generated by Mastra.
 *
 * @param profile - The SBPL profile content read from disk
 * @returns True when the content carries the generated-profile marker
 */
export function isGeneratedSeatbeltProfile(profile: string): boolean {
  return profile.includes(GENERATED_PROFILE_MARKER);
}

/**
 * Escape a path for use in SBPL profile.
 * Uses JSON.stringify for proper escaping.
 */
function escapePath(pathStr: string): string {
  return JSON.stringify(pathStr);
}

function canonicalizePath(pathStr: string): string {
  try {
    return realpathSync(pathStr);
  } catch {
    return pathStr;
  }
}

/**
 * Generate a seatbelt profile for the given configuration.
 *
 * The profile:
 * - Allows all file reads (can't restrict with subpath on macOS)
 * - Restricts file writes to temp directories, configured writable paths, and the workspace unless read-only
 * - Blocks network unless explicitly allowed
 *
 * @param workspacePath - The workspace directory to sandbox
 * @param config - Additional sandbox configuration
 * @returns The generated SBPL profile content
 */
export function generateSeatbeltProfile(workspacePath: string, config: NativeSandboxConfig): string {
  // Fail-closed: seatbelt cannot restrict process-exec, so reject unsupported config
  if (config.allowSystemBinaries === false) {
    throw new Error(
      'allowSystemBinaries: false is not supported by seatbelt (macOS). ' +
        'Use bubblewrap on Linux or remove this restriction.',
    );
  }

  const lines: string[] = [];

  // Version and default deny
  lines.push('(version 1)');
  // Marker comment, so a profile Mastra wrote is still known as ours on a later run.
  lines.push(GENERATED_PROFILE_MARKER);
  lines.push('(deny default (with message "mastra-sandbox"))');
  lines.push('');

  // Process permissions
  lines.push('; Process permissions');
  lines.push('(allow process-exec)');
  lines.push('(allow process-fork)');
  lines.push('(allow process-info* (target same-sandbox))');
  lines.push('(allow signal (target same-sandbox))');
  lines.push('');

  // Mach IPC
  lines.push('; Mach IPC');
  lines.push('(allow mach-lookup');
  for (const service of MACH_SERVICES) {
    lines.push(`  (global-name "${service}")`);
  }
  lines.push(')');
  lines.push('');

  // IPC
  lines.push('; IPC');
  lines.push('(allow ipc-posix-shm)');
  lines.push('(allow ipc-posix-sem)');
  lines.push('');

  // User preferences
  lines.push('; User preferences');
  lines.push('(allow user-preference-read)');
  lines.push('');

  // sysctl
  lines.push('; sysctl');
  lines.push('(allow sysctl-read)');
  lines.push('');

  // Device files
  // `file-ioctl` alone is not enough: opening a device node O_RDWR (as git,
  // ssh, and shell redirections do) also needs `file-write-data`, which the
  // default-deny profile otherwise withholds (write rules below are
  // workspace/temp-scoped).
  lines.push('; Device files');
  for (const device of ['/dev/null', '/dev/zero', '/dev/random', '/dev/urandom', '/dev/tty']) {
    lines.push(`(allow file-ioctl (literal "${device}"))`);
    lines.push(`(allow file-write-data (literal "${device}"))`);
  }
  lines.push('');

  // File read access - allow all reads (macOS limitation: can't use subpath without this)
  lines.push('; File read access (allow all - macOS sandbox limitation)');
  lines.push('(allow file-read*)');

  // Add custom read-only paths as additional allows (technically redundant but explicit)
  for (const p of config.readOnlyPaths ?? []) {
    lines.push(`(allow file-read* (subpath ${escapePath(p)}))`);
  }
  lines.push('');

  // File write access - restrict to workspace and temp
  lines.push('; File write access (restricted to workspace and temp)');

  const canonicalWorkspacePath = canonicalizePath(workspacePath);

  // Workspace
  if (!config.readOnly) {
    lines.push(`(allow file-write* (subpath ${escapePath(canonicalWorkspacePath)}))`);
  }

  // Temp directories (needed for many operations). When the workspace is read-only,
  // exclude it from these broad allows so temp-backed workspaces remain protected.
  for (const tempPath of ['/private/tmp', '/var/folders', '/private/var/folders']) {
    if (config.readOnly) {
      lines.push(
        `(allow file-write* (require-all (subpath ${escapePath(tempPath)}) (require-not (subpath ${escapePath(canonicalWorkspacePath)}))))`,
      );
    } else {
      lines.push(`(allow file-write* (subpath ${escapePath(tempPath)}))`);
    }
  }

  // Custom read-write paths
  for (const p of config.readWritePaths ?? []) {
    lines.push(`(allow file-write* (subpath ${escapePath(canonicalizePath(p))}))`);
  }
  lines.push('');

  // Network
  lines.push('; Network');
  if (config.allowNetwork) {
    lines.push('(allow network*)');
  } else {
    lines.push('(deny network* (with message "mastra-sandbox-network"))');
  }

  return lines.join('\n');
}

/**
 * Build the command arguments for sandbox-exec.
 *
 * Uses `-p` (inline profile) instead of `-f` (file) because
 * `-f` doesn't work reliably with path filters on modern macOS.
 *
 * @param command - The full shell command string to run
 * @param profile - The SBPL profile content (not a file path)
 * @returns Wrapped command and arguments for sandbox-exec
 */
export function buildSeatbeltCommand(command: string, profile: string): { command: string; args: string[] } {
  return {
    command: 'sandbox-exec',
    args: ['-p', profile, 'sh', '-c', command],
  };
}
